import React from 'react';
import classnames from 'classnames';
import { colord } from 'colord';
import { NullableChartDataPoint } from '../types';

import './ChartTooltip.scss';

type TooltipData = {
  nearestDatum?: {
    distance: number;
    index: number;
    key: string;
    datum: NullableChartDataPoint['values'];
  };
};

export interface ChartTooltipProps {
  /** Function to return more human-readable data. */
  formatter?: (data: string) => string;
  /** Display a series label while retaining its key for values and colours. */
  seriesLabelFormatter?: (key: string) => string;
  yValueFormatter?: (value: number) => string;
  tooltipData?: TooltipData;
  colorScale?: (dataKey: string) => string;
  /** Add units to toolips */
  yUnit?: string;
}

const identity = <T extends unknown>(x: T) => x;

/** A custom tooltip for area/bar/line charts. Shows only the nearest series. */

const ChartTooltip: React.FC<ChartTooltipProps> = ({
  formatter = identity,
  seriesLabelFormatter = identity,
  yValueFormatter = value => new Intl.NumberFormat().format(value),
  tooltipData,
  colorScale,
  yUnit,
}) => {
  const x = tooltipData?.nearestDatum?.key;
  const y = x && tooltipData?.nearestDatum?.datum[x];
  const stroke = x && colorScale?.(x);

  if (
    x === undefined ||
    typeof y !== 'number' ||
    !Number.isFinite(y) ||
    !stroke
  ) {
    return null;
  }

  const color = colord(stroke).alpha(0.1).toHex();

  return (
    <div
      className={classnames('chart-tooltip')}
      style={{
        borderColor: color,
      }}
    >
      <div
        className="header"
        style={{
          backgroundColor: color,
        }}
      >
        <h3>
          {tooltipData?.nearestDatum?.datum?.name
            ? formatter(tooltipData.nearestDatum.datum.name)
            : null}
        </h3>
      </div>
      <div className="info">
        <h4
          style={{
            color: stroke,
          }}
        >
          {yValueFormatter(y)}
          {yUnit}
        </h4>
        <small>{seriesLabelFormatter(x)}</small>
      </div>
    </div>
  );
};

export default ChartTooltip;

export const renderTooltip = (tooltipProps: ChartTooltipProps) => (
  <ChartTooltip {...tooltipProps} />
);
export type RenderTooltip = typeof renderTooltip;
